import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

/**
 * Carrito de cotización del catálogo público (run chefmax-cotizaciones-20261004).
 * Independiente de CartContext (productos propios / orders) — guarda items de
 * catalog_items con snapshot mínimo para la UI; el backend re-snapshottea
 * desde la DB al crear la solicitud (los precios del cliente no se confían).
 */

export interface QuoteCartItem {
  catalog_item_id: string;
  name: string;
  sku?: string;
  image?: string;
  supplier?: string;
  category?: string;
  price_usd?: number | null;
  qty: number;
}

interface QuoteCartContextType {
  items: QuoteCartItem[];
  count: number;
  addItem: (item: Omit<QuoteCartItem, 'qty'>, qty?: number) => void;
  removeItem: (catalogItemId: string) => void;
  updateQty: (catalogItemId: string, qty: number) => void;
  clear: () => void;
  has: (catalogItemId: string) => boolean;
}

const QuoteCartContext = createContext<QuoteCartContextType | undefined>(undefined);
const STORAGE_KEY = 'chefmax_quote_cart';

const load = (): QuoteCartItem[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((i) => i && i.catalog_item_id) : [];
  } catch {
    return [];
  }
};

export const QuoteCartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<QuoteCartItem[]>(load);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* storage lleno/bloqueado — no crítico */
    }
  }, [items]);

  const value = useMemo<QuoteCartContextType>(
    () => ({
      items,
      count: items.reduce((s, i) => s + i.qty, 0),
      addItem: (item, qty = 1) => {
        setItems((prev) => {
          const existing = prev.find((i) => i.catalog_item_id === item.catalog_item_id);
          if (existing) {
            return prev.map((i) =>
              i.catalog_item_id === item.catalog_item_id
                ? { ...i, qty: Math.min(999, i.qty + qty) }
                : i
            );
          }
          return [...prev, { ...item, qty: Math.min(999, Math.max(1, qty)) }];
        });
        toast.success(`${item.name} — agregado a la cotización`, { duration: 2000 });
      },
      removeItem: (catalogItemId) =>
        setItems((prev) => prev.filter((i) => i.catalog_item_id !== catalogItemId)),
      updateQty: (catalogItemId, qty) =>
        setItems((prev) =>
          qty <= 0
            ? prev.filter((i) => i.catalog_item_id !== catalogItemId)
            : prev.map((i) =>
                i.catalog_item_id === catalogItemId ? { ...i, qty: Math.min(999, qty) } : i
              )
        ),
      clear: () => setItems([]),
      has: (catalogItemId) => items.some((i) => i.catalog_item_id === catalogItemId),
    }),
    [items]
  );

  return <QuoteCartContext.Provider value={value}>{children}</QuoteCartContext.Provider>;
};

export const useQuoteCart = () => {
  const ctx = useContext(QuoteCartContext);
  if (!ctx) throw new Error('useQuoteCart must be used within a QuoteCartProvider');
  return ctx;
};
